// A small, generic debounce hook: framework-idiomatic version of the same
// "collapse rapid repeated updates into one, after things go quiet" policy
// debouncedSave.ts already applies to autosaving, for a caller that isn't
// saving anything - just wants an expensive downstream computation to stop
// re-running on every single keystroke/tick.
import { useEffect, useState } from 'react';

/**
 * Returns `value`, but only updates to a new one after `delayMs` has passed
 * with no further change - e.g. Model Studio's live CSG boolean preview
 * (editor-model.tsx) recomputing on every parameter edit, where fast typing
 * or holding a number input's spin arrows used to trigger a full,
 * expensive `three-bvh-csg` evaluation on every single change with no
 * batching at all.
 */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}
